/**
 * Web Speech API implementation of SpeechRecognitionAdapter.
 */
import { ashleyLog, ashleyWarn } from "../../debug/ashleySttDebug";
import { msSinceLastGesture, voiceDiag } from "../../debug/ashleyVoiceDiagnostics";

export function createWebSpeechRecognitionAdapter() {
  let recognition = null;
  let resultCallback = null;
  let errorCallback = null;
  let endCallback = null;

  function isSupported() {
    return (
      typeof window !== "undefined" &&
      ("SpeechRecognition" in window || "webkitSpeechRecognition" in window)
    );
  }

  function destroy() {
    if (recognition) {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onnomatch = null;
      recognition.onend = null;
      try {
        recognition.abort();
      } catch {
        // ignore
      }
      recognition = null;
    }
  }

  return {
    isSupported,
    onResult(callback) {
      resultCallback = callback;
    },
    onError(callback) {
      errorCallback = callback;
    },
    onEnd(callback) {
      endCallback = callback;
    },
    start(options = {}) {
      voiceDiag("CHAIN 7/8: adapter.start()", {
        hypothesisId: "H-CHAIN",
        location: "WebSpeechRecognitionAdapter.start",
        data: { msSinceGesture: msSinceLastGesture() },
      });

      if (!isSupported()) {
        ashleyWarn("SpeechRecognitionAdapter.start() — unsupported");
        errorCallback?.("unsupported");
        endCallback?.();
        return;
      }

      destroy();

      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition = new SpeechRecognition();
      recognition.continuous = options.continuous ?? false;
      recognition.interimResults = options.interimResults ?? false;
      recognition.lang = options.lang ?? "en-US";
      recognition.maxAlternatives = options.maxAlternatives ?? 1;

      ashleyLog("SpeechRecognitionAdapter.start() called", {
        continuous: recognition.continuous,
        interimResults: recognition.interimResults,
        lang: recognition.lang,
      });

      recognition.onstart = () => {
        ashleyLog("Recognition onstart");
        voiceDiag("Listening started (recognition.onstart)", {
          hypothesisId: "H-F",
          location: "WebSpeechRecognitionAdapter.onstart",
          data: { msSinceGesture: msSinceLastGesture() },
        });
      };

      recognition.onresult = (event) => {
        let transcript = "";
        const results = [];

        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const result = event.results[index];
          const text = result[0]?.transcript || "";
          results.push({
            index,
            transcript: text,
            isFinal: result.isFinal,
            confidence: result[0]?.confidence,
          });

          if (result.isFinal) {
            transcript += text;
          }
        }

        ashleyLog("Recognition onresult", {
          resultIndex: event.resultIndex,
          results,
          combinedFinal: transcript.trim() || null,
        });

        if (transcript.trim()) {
          resultCallback?.(transcript.trim(), true);
        }
      };

      recognition.onerror = (event) => {
        ashleyWarn("Recognition onerror", { code: event.error, message: event.message });
        voiceDiag(`Speech error: ${event.error || "unknown"}`, {
          hypothesisId: "H-F",
          location: "WebSpeechRecognitionAdapter.onerror",
          data: {
            code: event.error,
            message: event.message,
            msSinceGesture: msSinceLastGesture(),
          },
        });
        if (event.error === "not-allowed") {
          voiceDiag(
            "Microphone permission blocked — enable in Chrome site settings",
            {
              hypothesisId: "H-F",
              location: "WebSpeechRecognitionAdapter.onerror",
            },
          );
        }
        errorCallback?.(event.error || "unknown");
      };

      recognition.onnomatch = () => {
        ashleyWarn("Recognition onnomatch");
      };

      recognition.onend = () => {
        ashleyLog("Recognition onend");
        endCallback?.();
      };

      voiceDiag("CHAIN 8/8: recognition.start()", {
        hypothesisId: "H-CHAIN",
        location: "WebSpeechRecognitionAdapter.start.preRecognitionStart",
        data: { msSinceGesture: msSinceLastGesture() },
      });

      try {
        recognition.start();
        voiceDiag("CHAIN 8/8: recognition.start() returned (sync)", {
          hypothesisId: "H-CHAIN",
          location: "WebSpeechRecognitionAdapter.start.postRecognitionStart",
          data: { msSinceGesture: msSinceLastGesture() },
        });
      } catch (error) {
        ashleyWarn("SpeechRecognitionAdapter.start() threw", {
          message: error?.message || String(error),
        });
        voiceDiag(`CHAIN 8/8: recognition.start() THREW — ${error?.message || String(error)}`, {
          hypothesisId: "H-CHAIN",
          location: "WebSpeechRecognitionAdapter.start.catch",
          data: { msSinceGesture: msSinceLastGesture() },
        });
        errorCallback?.("start-failed");
        endCallback?.();
      }
    },
    stop() {
      if (recognition) {
        recognition.stop();
      }
    },
    abort() {
      destroy();
    },
    destroy,
  };
}
